import { useCallback, useEffect, useRef, useState } from 'react';
import { loadImage } from './loadImage';

export type TrainResult = { accuracy: number; labels: string[] };
export type ClassifyResult = { label: string; confidence: number }[];
export type TrainingSample = { url: string; label: string };
export interface ImageModel {
  status: 'idle' | 'loading' | 'training' | 'ready' | 'error';
  progress: number;
  accuracy: number | null;
  evalCount: number | null;
  isReady: boolean;
  train: (samples: TrainingSample[]) => Promise<TrainResult>;
  classify: (imgEl: HTMLImageElement) => Promise<ClassifyResult>;
  reset: () => void;
}

type Classifier = {
  addImage: (image: HTMLImageElement, label: string) => Promise<unknown>;
  train: (callback: (loss: number | null) => void) => Promise<unknown>;
  classify: (
    image: HTMLImageElement,
    callback: (error: Error | null, results?: Array<{ label: string; confidence: number }>) => void,
  ) => void;
};

export function useImageModel(): ImageModel {
  const [status, setStatus] = useState<ImageModel['status']>('idle');
  const [progress, setProgress] = useState(0);
  const [accuracy, setAccuracy] = useState<number | null>(null);
  const [evalCount, setEvalCount] = useState<number | null>(null);
  const mounted = useRef(true);
  const classifierRef = useRef<Classifier | null>(null);

  useEffect(
    () => () => {
      mounted.current = false;
    },
    [],
  );

  const train = useCallback(async (samples: TrainingSample[]): Promise<TrainResult> => {
    const usable = samples.filter((sample) => sample.label.trim() && sample.url);
    const labels = Array.from(new Set(usable.map((sample) => sample.label)));
    if (labels.length < 2) throw new Error('Ajoutez au moins deux labels avant de lancer l’entraînement.');
    if (labels.some((label) => !usable.some((sample) => sample.label === label))) {
      throw new Error('Chaque label doit contenir au moins une image.');
    }
    if (mounted.current) {
      setStatus('loading');
      setProgress(0);
      setAccuracy(null);
    }
    try {
      // ml5 is intentionally lazy-loaded so TensorFlow is kept out of the main chunk.
      // @ts-ignore ml5@0.12.2 has no bundled TypeScript declarations.
      const ml5Module = await import('ml5');
      const ml5 = (ml5Module as any).default ?? ml5Module;
      const featureExtractor = await new Promise<any>((resolve, reject) => {
        try {
          // ml5 fixe numLabels à 2 par défaut : sans cette option, un 3ᵉ label n'est jamais appris.
          const extractor = ml5.featureExtractor('MobileNet', { numLabels: labels.length }, () =>
            resolve(extractor),
          );
          if (!extractor) reject(new Error('ml5 n’a pas fourni de feature extractor.'));
        } catch (error) {
          reject(error);
        }
      });
      const classifier: Classifier = featureExtractor.classification();
      classifierRef.current = classifier;
      const images = await Promise.all(
        usable.map(async (sample) => ({ sample, image: await loadImage(sample.url) })),
      );
      const byLabel = new Map<string, typeof images>();
      images.forEach((item) =>
        byLabel.set(item.sample.label, [...(byLabel.get(item.sample.label) || []), item]),
      );
      const evaluation: typeof images = [];
      byLabel.forEach((group) => {
        if (group.length >= 2) evaluation.push(group.pop() as (typeof images)[number]);
      });
      const training = images.filter((item) => !evaluation.includes(item));
      const trainSet = training.length ? training : images;
      await Promise.all(trainSet.map((item) => classifier.addImage(item.image, item.sample.label)));
      if (mounted.current) setStatus('training');
      await classifier.train((loss) => {
        if (mounted.current) {
          setProgress(
            loss == null ? 100 : Math.min(99, Math.max(1, Math.round(100 / (1 + Math.max(0, loss))))),
          );
        }
      });
      const evalSet = evaluation.length ? evaluation : trainSet;
      let correct = 0;
      for (const item of evalSet) {
        const result = await new Promise<ClassifyResult>((resolve, reject) =>
          classifier.classify(item.image, (error, results) =>
            error
              ? reject(error)
              : resolve(
                  (results || [])
                    .map((result) => ({ label: result.label, confidence: Number(result.confidence) }))
                    .sort((a, b) => b.confidence - a.confidence),
                ),
          ),
        );
        if (result[0]?.label === item.sample.label) correct += 1;
      }
      const result = Math.round((100 * correct) / evalSet.length);
      if (mounted.current) {
        setProgress(100);
        setAccuracy(result);
        setEvalCount(evalSet.length);
        setStatus('ready');
      }
      return { accuracy: result, labels };
    } catch (error) {
      if (mounted.current) setStatus('error');
      throw error;
    }
  }, []);

  const classify = useCallback(async (imgEl: HTMLImageElement): Promise<ClassifyResult> => {
    if (!classifierRef.current) throw new Error('Entraînez d’abord le modèle avant de tester une image.');
    return new Promise((resolve, reject) =>
      classifierRef.current?.classify(imgEl, (error, results) =>
        error
          ? reject(error)
          : resolve(
              (results || [])
                .map((result) => ({ label: result.label, confidence: Number(result.confidence) }))
                .sort((a, b) => b.confidence - a.confidence),
            ),
      ),
    );
  }, []);

  const reset = useCallback(() => {
    classifierRef.current = null;
    if (mounted.current) {
      setStatus('idle');
      setProgress(0);
      setAccuracy(null);
      setEvalCount(null);
    }
  }, []);
  return {
    status,
    progress,
    accuracy,
    evalCount,
    isReady: status === 'ready' && !!classifierRef.current,
    train,
    classify,
    reset,
  };
}
